import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import * as signalR from '@microsoft/signalr'
import { Room as LiveKitRoom, RoomEvent, Track, VideoPresets } from 'livekit-client'
import { useAuth } from '../../context/AuthContext'
import Participant from './Participant'
import {
  ArrowLeft,
  Brain,
  Camera,
  CameraOff,
  CheckCircle2,
  Copy,
  Link2,
  Loader2,
  Menu,
  MessageSquareText,
  Mic,
  MonitorUp,
  Plus,
  Send,
  SmilePlus,
  X
} from 'lucide-react'

const initialTaskState = { title: '', description: '', dueDate: '', sending: false, error: '' }
const initialNoteState = { title: '', content: '', sending: false, error: '' }
const initialInviteState = { email: '', sending: false, error: '', success: '' }
const initialAiModalState = { open: false, question: '', sending: false, error: '', visibleAnswer: '', streaming: false }
const initialQuickComposeState = { text: '', mode: 'chat', sending: false, error: '' }
const quickComposeModes = ['chat', 'task', 'note', 'ai']
const quickEmojis = ['😀', '🔥', '👏', '🚀', '💡', '✅', '🎯', '❤️', '🙏', '😂', '🤝', '📌']
const MOBILE_CONVERSATION_HEIGHT = 200
const MOBILE_COMPOSER_HEIGHT = 100
const DEFAULT_STAGE_RATIO = 16 / 9
const isBrainRtcDebugEnabled = true
const brainRtcLog = (event, details = {}) => {
  if (!isBrainRtcDebugEnabled) return
  console.log(`[BrainRTC] ${event}`, details)
}
const MAX_RTC_DEBUG_EVENTS = 20

const buildClientMessageId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `chat-${crypto.randomUUID()}`
  }

  return `chat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

const isPrivateDevelopmentHost = (hostname) => {
  if (!hostname) return false
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true

  const match = hostname.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/)
  if (!match) return false

  const [firstOctet, secondOctet] = match.slice(1, 3).map(Number)
  return firstOctet === 10
    || (firstOctet === 172 && secondOctet >= 16 && secondOctet <= 31)
    || (firstOctet === 192 && secondOctet === 168)
}

const getLocalhostGuestIpOverride = () => {
  if (typeof window === 'undefined') return null

  const hostname = window.location.hostname?.toLowerCase()
  if (!isPrivateDevelopmentHost(hostname)) return null

  const storageKey = 'brain-room-localhost-ip-override'
  const existing = window.sessionStorage.getItem(storageKey)
  if (existing) return existing

  const randomOctet = () => Math.floor(Math.random() * 200) + 20
  const simulatedIp = `10.${randomOctet()}.${randomOctet()}.${randomOctet()}`
  window.sessionStorage.setItem(storageKey, simulatedIp)
  return simulatedIp
}

const normalizeUserId = (value) => String(value || '').trim().toLowerCase()

const getTimelineItemKey = (item) => item?.id || item?.clientMessageId || `${item?.authorId || item?.author || 'unknown'}:${item?.message || ''}:${item?.at || ''}`

const mergeTimelineItems = (...collections) => {
  const ordered = []
  const seen = new Set()

  collections.flat().forEach((item) => {
    if (!item) return
    const key = getTimelineItemKey(item)
    if (!key || seen.has(key)) return
    seen.add(key)
    ordered.push(item)
  })

  return ordered
}

const mergeUniqueParticipants = (...collections) => {
  const ordered = []
  const seen = new Set()

  collections.flat().forEach((participant) => {
    if (!participant?.id) return
    const key = normalizeUserId(participant.id)
    if (!key || seen.has(key)) return
    seen.add(key)
    ordered.push(participant)
  })

  return ordered
}

const liveStyleKeyframes = `
@keyframes live-slide-up {
  0% { opacity: 0; transform: translate(-22px, 16px) scale(0.96); }
  100% { opacity: 1; transform: translate(0, 0) scale(1); }
}

.brain-live-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(251, 191, 36, 0.35) rgba(15, 23, 42, 0.18);
}

.brain-live-scroll::-webkit-scrollbar {
  width: 8px;
}

.brain-live-scroll::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.16);
  border-radius: 999px;
}

.brain-live-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, rgba(251, 191, 36, 0.55), rgba(148, 163, 184, 0.38));
  border-radius: 999px;
}

.brain-room-stage-shell {
  --brain-stage-left-reserve: 0px;
  --brain-stage-right-reserve: 0px;
  --brain-stage-gutter: 0px;
  --brain-stage-compose-max: 920px;
}

@media (min-width: 768px) {
  .brain-room-stage-shell {
    --brain-stage-left-reserve: clamp(228px, 20vw, 292px);
    --brain-stage-right-reserve: clamp(76px, 7vw, 112px);
    --brain-stage-gutter: clamp(14px, 1.8vw, 28px);
    --brain-stage-compose-max: min(920px, calc(100% - var(--brain-stage-left-reserve) - var(--brain-stage-right-reserve) - var(--brain-stage-gutter)));
  }

  .brain-room-stage-feed {
    width: var(--brain-stage-left-reserve);
  }

  .brain-room-stage-main {
    padding-left: calc(var(--brain-stage-left-reserve) + var(--brain-stage-gutter));
    padding-right: calc(var(--brain-stage-right-reserve) + var(--brain-stage-gutter));
  }

  .brain-room-stage-main-inner {
    max-width: min(100%, clamp(700px, 66vw, 1120px));
  }

  .brain-room-stage-title {
    padding-left: calc(var(--brain-stage-left-reserve) + var(--brain-stage-gutter));
    padding-right: calc(var(--brain-stage-right-reserve) + var(--brain-stage-gutter));
  }

  .brain-room-stage-compose {
    padding-left: calc(var(--brain-stage-left-reserve) + var(--brain-stage-gutter));
    padding-right: calc(var(--brain-stage-right-reserve) + var(--brain-stage-gutter));
  }

  .brain-room-stage-compose-card {
    max-width: var(--brain-stage-compose-max);
  }
}

@media (max-width: 767px) {
  body.brain-room-mobile-focus {
    overflow: hidden;
  }

  body.brain-room-mobile-focus header {
    display: none !important;
  }

  body.brain-room-mobile-focus main,
  body.brain-room-mobile-focus #root {
    height: 100svh;
    min-height: 100svh;
    overflow: hidden;
  }

  .brain-room-mobile-feed {
    background: linear-gradient(180deg, rgba(2, 6, 23, 0.92), rgba(15, 23, 42, 0.86));
    border-color: rgba(255, 255, 255, 0.12);
    box-shadow: 0 18px 40px rgba(2, 6, 23, 0.24);
  }
}
`

export default function BrainEmprendedorRoom() {
  const { user, loading: authLoading, isAuthenticated, checkAuth } = useAuth()
  const navigate = useNavigate()
  const { roomId } = useParams()
  const [room, setRoom] = useState(null)
  const [roomPreview, setRoomPreview] = useState(null)
  const [invitees, setInvitees] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [guestState, setGuestState] = useState({ name: '', joining: false, error: '' })
  const [activeComposer, setActiveComposer] = useState('')
  const [taskState, setTaskState] = useState(initialTaskState)
  const [noteState, setNoteState] = useState(initialNoteState)
  const [inviteState, setInviteState] = useState(initialInviteState)
  const [feedbackState, setFeedbackState] = useState({ sending: false, error: '' })
  const [aiModal, setAiModal] = useState(initialAiModalState)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [liveFeed, setLiveFeed] = useState([])
  const [quickCompose, setQuickCompose] = useState(initialQuickComposeState)
  const [mediaState, setMediaState] = useState({ cameraOn: false, broadcasting: false, error: '' })
  const [activeSpeakerId, setActiveSpeakerId] = useState('')
  const [meetParticipantStates, setMeetParticipantStates] = useState({})
  const [meetConnectionState, setMeetConnectionState] = useState({ status: 'idle', error: '' })
  const [rtcDebugFeed, setRtcDebugFeed] = useState([])
  const [connectedParticipantIds, setConnectedParticipantIds] = useState([])
  const typingIntervalRef = useRef(null)
  const stageViewportRef = useRef(null)
  const hubConnectionRef = useRef(null)
  const liveKitRoomRef = useRef(null)
  const mediaStateRef = useRef(mediaState)
  const participantsRef = useRef([])
  const [stageViewportRatio, setStageViewportRatio] = useState(DEFAULT_STAGE_RATIO)

  const loadPublicPreviewData = async (signal) => {
    const response = await fetch(`/api/brain/public/rooms/${roomId}/preview`, { signal })
    if (!response.ok) throw new Error('No se pudo cargar la room.')

    const payload = await response.json()
    setRoom(null)
    setRoomPreview(payload)
    setError('')
    return payload
  }

  const loadAuthenticatedRoomData = async (signal) => {
    const [roomResponse, inviteesResponse] = await Promise.all([
      fetch(`/api/brain/rooms/${roomId}`, { credentials: 'include', signal }),
      fetch(`/api/brain/rooms/${roomId}/invitees`, { credentials: 'include', signal })
    ])

    if (roomResponse.status === 403 || roomResponse.status === 404) {
      await loadPublicPreviewData(signal)
      return null
    }

    if (!roomResponse.ok) throw new Error('No se pudo cargar la room.')
    if (!inviteesResponse.ok) throw new Error('No se pudieron cargar usuarios para invitar.')

    const roomPayload = await roomResponse.json()
    const inviteesPayload = await inviteesResponse.json()
    appendRtcDebugEvent('room_data_loaded', {
      roomId: roomPayload?.id,
      participants: roomPayload?.participants?.length || 0,
      timeline: roomPayload?.timeline?.length || 0
    })

    setRoom((current) => ({
      ...current,
      ...roomPayload,
      timeline: mergeTimelineItems(current?.timeline || [], roomPayload.timeline || [])
    }))
    setRoomPreview(roomPayload)
    setInvitees(inviteesPayload)
    setLiveFeed((current) => mergeLiveFeed(current, buildInitialLiveFeed(roomPayload)))
    setConnectedParticipantIds((current) => {
      if (current.length > 0) return current
      const participantIds = (roomPayload?.participants || [])
        .map((participant) => normalizeUserId(participant?.id))
        .filter(Boolean)
      return Array.from(new Set(participantIds))
    })
    setError('')

    return roomPayload
  }

  const appendRtcDebugEvent = (event, details = {}) => {
    brainRtcLog(event, details)
    if (!isBrainRtcDebugEnabled) return

    const timestamp = new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })

    setRtcDebugFeed((current) => [
      {
        id: `${event}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        event,
        timestamp,
        details: JSON.stringify(details)
      },
      ...current
    ].slice(0, MAX_RTC_DEBUG_EVENTS))
  }

  const syncMeetParticipantState = (liveKitRoom) => {
    if (!liveKitRoom) return

    const nextStates = {}
    const collectParticipantState = (participant) => {
      if (!participant?.identity) return

      const normalizedParticipantId = normalizeUserId(participant.identity)
      const parsedMetadata = parseParticipantMetadata(participant.metadata)
      const fallbackName = participant.name || parsedMetadata.displayName || participant.identity
      const cameraPublication = participant.getTrackPublication(Track.Source.Camera)
      const microphonePublication = participant.getTrackPublication(Track.Source.Microphone)
      const screenSharePublication = participant.getTrackPublication(Track.Source.ScreenShare)
      const videoTrack = screenSharePublication?.videoTrack || cameraPublication?.videoTrack || cameraPublication?.track || null
      const audioTrack = microphonePublication?.audioTrack || microphonePublication?.track || null

      nextStates[normalizedParticipantId] = {
        id: participant.identity,
        name: fallbackName,
        initials: parsedMetadata.initials || getInitialsFromName(fallbackName),
        role: parsedMetadata.role || (normalizedParticipantId === currentUserId ? 'Tu usuario' : 'Participante'),
        videoTrack,
        audioTrack,
        cameraOn: !!cameraPublication && !cameraPublication.isMuted,
        microphoneOn: !!microphonePublication && !microphonePublication.isMuted,
        broadcasting: !!videoTrack || !!audioTrack
      }
    }

    collectParticipantState(liveKitRoom.localParticipant)
    liveKitRoom.remoteParticipants.forEach((participant) => {
      collectParticipantState(participant)
    })

    setMeetParticipantStates(nextStates)

    const localParticipantState = nextStates[currentUserId] || {}
    const nextMediaState = {
      cameraOn: !!localParticipantState.cameraOn,
      broadcasting: !!localParticipantState.broadcasting,
      error: ''
    }
    mediaStateRef.current = nextMediaState
    setMediaState((current) => ({ ...current, ...nextMediaState }))
  }

  useEffect(() => {
    if (authLoading) return

    const controller = new AbortController()

    const loadAuthenticatedRoom = async () => {
      try {
        await loadAuthenticatedRoomData(controller.signal)
        setError('')
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message || 'No se pudo cargar la room.')
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    setLoading(true)
    if (isAuthenticated) {
      loadAuthenticatedRoom()
    } else {
      loadPublicPreviewData(controller.signal)
        .catch((err) => {
          if (err.name !== 'AbortError') setError(err.message || 'No se pudo cargar la room.')
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false)
        })
    }

    return () => {
      controller.abort()
      if (typingIntervalRef.current) window.clearInterval(typingIntervalRef.current)
    }
  }, [authLoading, isAuthenticated, roomId])

  useEffect(() => {
    if (!isAuthenticated || !room) return

    const controller = new AbortController()
    const intervalId = window.setInterval(() => {
      loadAuthenticatedRoomData(controller.signal).catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('Error refreshing room data:', err)
        }
      })
    }, 4000)

    return () => {
      controller.abort()
      window.clearInterval(intervalId)
    }
  }, [isAuthenticated, roomId, room])

  useEffect(() => {
    if (!isAuthenticated || !room || typeof document === 'undefined') return undefined

    document.body.classList.add('brain-room-mobile-focus')
    return () => {
      document.body.classList.remove('brain-room-mobile-focus')
    }
  }, [isAuthenticated, room])

  const rawCurrentUserId = user?.id || user?.Id || ''
  const currentUserId = normalizeUserId(rawCurrentUserId)
  const currentUserName = `${user?.first_name || user?.FirstName || ''} ${user?.last_name || user?.LastName || ''}`.trim() || user?.email || 'Tu usuario'
  const getParticipantMedia = (participantId) => meetParticipantStates[normalizeUserId(participantId)] || {}
  const participantHasVideo = (participantId) => Boolean(getParticipantMedia(participantId).videoTrack)
  const participantHasAudio = (participantId) => Boolean(getParticipantMedia(participantId).audioTrack)
  const participants = room?.participants || []
  const visibleParticipantIdSet = new Set(
    [
      ...connectedParticipantIds,
      currentUserId
    ].filter(Boolean)
  )
  const visibleParticipants = participants.filter((participant) => visibleParticipantIdSet.has(normalizeUserId(participant?.id)))
  const invitationItems = room?.invitations || []
  const maxParticipants = room?.maxParticipants || 5
  const hostParticipant = participants.find((participant) => participant.role === 'Host') || participants[0] || null
  const hasRoomAccess = !!room
  const participantByNormalizedId = new Map(
    visibleParticipants
      .filter((participant) => participant?.id)
      .map((participant) => [normalizeUserId(participant.id), participant])
  )
  const participantOrderById = new Map(
    visibleParticipants
      .filter((participant) => participant?.id)
      .map((participant, index) => [normalizeUserId(participant.id), index])
  )
  const participantTiles = mergeUniqueParticipants(
    visibleParticipants,
    ...(currentUserId && !participantByNormalizedId.has(currentUserId)
      ? [{
          id: rawCurrentUserId || currentUserId,
          name: currentUserName,
          initials: getInitialsFromName(currentUserName)
        }]
      : []),
    ...Object.keys(meetParticipantStates).map((participantId) => participantByNormalizedId.get(participantId) || {
      id: participantId,
      name: meetParticipantStates[participantId]?.name || 'Participante',
      initials: getInitialsFromName(meetParticipantStates[participantId]?.name || 'Participante')
    })
  ).sort((left, right) => {
    const leftId = normalizeUserId(left?.id)
    const rightId = normalizeUserId(right?.id)
    const leftIsLocal = leftId === currentUserId
    const rightIsLocal = rightId === currentUserId
    const leftHasMedia = participantHasVideo(leftId) || participantHasAudio(leftId)
    const rightHasMedia = participantHasVideo(rightId) || participantHasAudio(rightId)

    if (leftHasMedia !== rightHasMedia) return leftHasMedia ? -1 : 1
    if (leftIsLocal !== rightIsLocal) return leftIsLocal ? -1 : 1

    const leftOrder = participantOrderById.get(leftId)
    const rightOrder = participantOrderById.get(rightId)

    if (typeof leftOrder === 'number' && typeof rightOrder === 'number') return leftOrder - rightOrder
    if (typeof leftOrder === 'number') return -1
    if (typeof rightOrder === 'number') return 1
    return String(left?.name || '').localeCompare(String(right?.name || ''))
  })
  const broadcastingParticipants = visibleParticipants.filter((participant) => {
    const participantId = normalizeUserId(participant?.id)
    if (!participantId) return false
    return participantHasVideo(participantId) || !!getParticipantMedia(participantId)?.broadcasting
  })
  const broadcastingParticipant = broadcastingParticipants[0] || null
  const currentSpeaker = visibleParticipants.find((participant) => normalizeUserId(participant.id) === activeSpeakerId) || broadcastingParticipant || hostParticipant || visibleParticipants[0] || null
  const hasParticipantGrid = participantTiles.length > 0
  const stageLayout = buildParticipantGridLayout(participantTiles.length, stageViewportRatio)
  const joinUrl = room?.joinUrl || (typeof window !== 'undefined' ? `${window.location.origin}/brain-emprendedor/rooms/${roomId}` : '')
  const mobileViewportHeight = typeof window !== 'undefined' ? (window.visualViewport?.height || window.innerHeight || 0) : 0
  const mobileConversationHeight = MOBILE_CONVERSATION_HEIGHT
  const mobileVideoHeight = Math.max(0, mobileViewportHeight - mobileConversationHeight - MOBILE_COMPOSER_HEIGHT)
  const mobileStageInlineStyle = typeof window !== 'undefined' && window.innerWidth < 768
    ? { gridTemplateRows: `${mobileVideoHeight}px ${mobileConversationHeight}px ${MOBILE_COMPOSER_HEIGHT}px` }
    : undefined

  useEffect(() => {
    participantsRef.current = visibleParticipants
  }, [visibleParticipants])

  useEffect(() => {
    const element = stageViewportRef.current
    if (!element || typeof ResizeObserver === 'undefined') return undefined

    const updateStageViewportRatio = () => {
      const nextWidth = element.clientWidth || 0
      const nextHeight = element.clientHeight || 0
      if (!nextWidth || !nextHeight) return
      setStageViewportRatio(nextWidth / nextHeight)
    }

    updateStageViewportRatio()

    const observer = new ResizeObserver(() => {
      updateStageViewportRatio()
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [hasParticipantGrid, participantTiles.length])

  useEffect(() => {
    if (hasRoomAccess) return

    setGuestState((current) => {
      if (current.name.trim()) return current
      return {
        ...current,
        name: currentUserName === 'Tu usuario' ? '' : currentUserName
      }
    })
  }, [currentUserName, hasRoomAccess])

  useEffect(() => {
    if (!isAuthenticated || !roomId || !room) return

    let isMounted = true

    const connectToRoomHub = async () => {
      appendRtcDebugEvent('hub_connecting', { roomId, currentUserId, currentUserName })
      const connection = new signalR.HubConnectionBuilder()
        .withUrl('/hubs/brain-room', { withCredentials: true })
        .withAutomaticReconnect()
        .build()

      connection.on('ChatMessageCreated', (message) => {
        appendRtcDebugEvent('signal_chat_message_created', { messageId: message?.id, authorId: message?.authorId })
        if (!isMounted) return

        setRoom((current) => {
          if (!current) return current

          return {
            ...current,
            timeline: mergeTimelineItems(current.timeline || [], [message])
          }
        })

        pushLiveItem({
          id: message.id,
          kind: 'user',
          label: message.author || 'Nuevo mensaje',
          text: `${message.author}: ${message.message}`
        })
      })

      connection.on('RoomStateChanged', () => {
        appendRtcDebugEvent('signal_room_state_changed', { roomId })
        if (!isMounted) return
        loadAuthenticatedRoomData(undefined).catch((err) => {
          console.error('Error refreshing room after signal:', err)
        })
      })

      connection.on('ParticipantJoined', (payload) => {
        appendRtcDebugEvent('signal_participant_joined', payload || {})
        const joinedUserId = normalizeUserId(payload?.userId)
        if (!isMounted || !joinedUserId || joinedUserId === currentUserId) return

        loadAuthenticatedRoomData(undefined).catch((err) => {
          console.error('Error refreshing participants after join:', err)
        })
      })

      connection.on('ParticipantLeft', (payload) => {
        appendRtcDebugEvent('signal_participant_left', payload || {})
        const leftUserId = normalizeUserId(payload?.userId)
        if (!isMounted || !leftUserId) return
        loadAuthenticatedRoomData(undefined).catch((err) => {
          console.error('Error refreshing participants after leave:', err)
        })
      })

      connection.on('ConnectedParticipantsChanged', (payload) => {
        appendRtcDebugEvent('signal_connected_participants_changed', payload || {})
        const nextIds = (payload?.userIds || [])
          .map((userId) => normalizeUserId(userId))
          .filter(Boolean)
        setConnectedParticipantIds(Array.from(new Set(nextIds)))
        loadAuthenticatedRoomData(undefined).catch((err) => {
          console.error('Error refreshing room after connected participants change:', err)
        })
      })

      try {
        await connection.start()
        appendRtcDebugEvent('hub_connected', { connectionId: connection.connectionId, roomId, currentUserId })
        await connection.invoke('JoinRoom', roomId, rawCurrentUserId, currentUserName)
        appendRtcDebugEvent('hub_join_room_invoked', { roomId, currentUserId, currentUserName })
        hubConnectionRef.current = connection
      } catch (err) {
        appendRtcDebugEvent('hub_connection_error', { message: err?.message || String(err) })
        console.error('Error connecting to room hub:', err)
      }
    }

    connectToRoomHub()

    return () => {
      isMounted = false
      const connection = hubConnectionRef.current
      hubConnectionRef.current = null
      if (connection) {
        connection.invoke('LeaveRoom', roomId, rawCurrentUserId).catch(() => {})
        connection.stop().catch(() => {})
      }
    }
  }, [isAuthenticated, roomId, currentUserId, currentUserName, rawCurrentUserId])

  useEffect(() => {
    if (!isAuthenticated || !roomId || !room) return undefined

    let isMounted = true
    const liveKitRoom = new LiveKitRoom({
      adaptiveStream: true,
      dynacast: true,
      videoCaptureDefaults: {
        resolution: VideoPresets.h720.resolution
      }
    })

    const syncRoomState = () => {
      if (!isMounted) return
      syncMeetParticipantState(liveKitRoom)
    }

    const connectToMeet = async () => {
      setMeetConnectionState({ status: 'connecting', error: '' })
      appendRtcDebugEvent('meet_connecting', { roomId, currentUserId })

      try {
        const response = await fetch(`/api/brain/rooms/${roomId}/meet/session`, {
          method: 'POST',
          credentials: 'include'
        })

        const payload = await response.json().catch(() => null)
        if (!response.ok) {
          throw new Error(payload?.message || 'No fue posible abrir la meet de esta room.')
        }

        liveKitRoom
          .on(RoomEvent.ParticipantConnected, syncRoomState)
          .on(RoomEvent.ParticipantDisconnected, syncRoomState)
          .on(RoomEvent.TrackSubscribed, syncRoomState)
          .on(RoomEvent.TrackUnsubscribed, syncRoomState)
          .on(RoomEvent.TrackMuted, syncRoomState)
          .on(RoomEvent.TrackUnmuted, syncRoomState)
          .on(RoomEvent.LocalTrackPublished, syncRoomState)
          .on(RoomEvent.LocalTrackUnpublished, syncRoomState)
          .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
            if (!isMounted) return
            const nextSpeakerId = normalizeUserId(speakers?.[0]?.identity)
            if (nextSpeakerId) {
              setActiveSpeakerId(nextSpeakerId)
            }
          })
          .on(RoomEvent.ConnectionStateChanged, (state) => {
            if (!isMounted) return
            appendRtcDebugEvent('meet_connection_state_changed', { state })
            setMeetConnectionState({ status: state, error: '' })
            syncRoomState()
          })
          .on(RoomEvent.MediaDevicesError, (error) => {
            if (!isMounted) return
            const nextMediaState = {
              ...mediaStateRef.current,
              error: error?.message || 'No se pudo acceder a la camara o al microfono.'
            }
            mediaStateRef.current = nextMediaState
            setMediaState(nextMediaState)
          })

        liveKitRoomRef.current = liveKitRoom
        liveKitRoom.prepareConnection(payload.serverUrl, payload.token)
        await liveKitRoom.connect(payload.serverUrl, payload.token)

        if (!isMounted) {
          await liveKitRoom.disconnect()
          return
        }

        syncRoomState()
        setMeetConnectionState({ status: 'connected', error: '' })
        appendRtcDebugEvent('meet_connected', { roomId, currentUserId })
      } catch (err) {
        const message = err?.message || 'No fue posible conectar la meet.'
        appendRtcDebugEvent('meet_connection_error', { message })
        setMeetConnectionState({ status: 'error', error: message })
        const nextMediaState = { ...mediaStateRef.current, error: message }
        mediaStateRef.current = nextMediaState
        setMediaState(nextMediaState)
      }
    }

    connectToMeet()

    return () => {
      isMounted = false
      if (liveKitRoomRef.current === liveKitRoom) {
        liveKitRoomRef.current = null
      }
      setMeetParticipantStates({})
      liveKitRoom.disconnect()
    }
  }, [isAuthenticated, room, roomId, currentUserId])

  useEffect(() => {
    if (!hostParticipant?.id) return

    const normalizedBroadcastingParticipantId = normalizeUserId(broadcastingParticipant?.id)

    if (normalizedBroadcastingParticipantId && activeSpeakerId !== normalizedBroadcastingParticipantId) {
      setActiveSpeakerId(normalizedBroadcastingParticipantId)
      return
    }

    if (!activeSpeakerId || !visibleParticipants.some((participant) => normalizeUserId(participant.id) === activeSpeakerId)) {
      setActiveSpeakerId(normalizeUserId(hostParticipant.id))
    }
  }, [visibleParticipants, activeSpeakerId, hostParticipant?.id, broadcastingParticipant?.id])

  useEffect(() => {
    const canonicalRoomId = room?.id || roomPreview?.id
    if (!canonicalRoomId || canonicalRoomId === roomId) return

    navigate(`/brain-emprendedor/rooms/${canonicalRoomId}`, { replace: true })
  }, [navigate, room?.id, roomPreview?.id, roomId])

  useEffect(() => {
    mediaStateRef.current = mediaState
  }, [mediaState])

  const pushLiveItem = (item) => {
    setLiveFeed((current) => [item, ...current].slice(0, 8))
  }

  const setMeetMediaEnabled = async (enabled) => {
    const liveKitRoom = liveKitRoomRef.current
    if (!liveKitRoom) {
      const fallbackMessage = meetConnectionState.error || 'La meet aun no esta lista. Espera la conexion de video.'
      const nextMediaState = { ...mediaStateRef.current, error: fallbackMessage }
      mediaStateRef.current = nextMediaState
      setMediaState(nextMediaState)
      return
    }

    try {
      setMeetConnectionState((current) => ({ ...current, error: '' }))
      await liveKitRoom.localParticipant.setCameraEnabled(enabled, enabled
        ? {
            resolution: VideoPresets.h720.resolution
          }
        : undefined)
      await liveKitRoom.localParticipant.setMicrophoneEnabled(enabled)
      syncMeetParticipantState(liveKitRoom)

      if (enabled) {
        setActiveSpeakerId(currentUserId)
        pushLiveItem({ id: `broadcast-${Date.now()}`, kind: 'user', label: 'Camara activada', text: 'Tu video y audio ya estan disponibles para la room.' })
      } else {
        if (hostParticipant?.id) setActiveSpeakerId(normalizeUserId(hostParticipant.id))
        pushLiveItem({ id: `camera-off-${Date.now()}`, kind: 'user', label: 'Camara apagada', text: 'Tu audio y video se detuvieron en la meet.' })
      }
    } catch (err) {
      const nextMediaState = {
        ...mediaStateRef.current,
        error: err?.message || 'No se pudo cambiar el estado de tu camara o microfono.'
      }
      mediaStateRef.current = nextMediaState
      setMediaState(nextMediaState)
    }
  }

  const toggleBroadcast = async () => {
    await setMeetMediaEnabled(!(mediaState.broadcasting || mediaState.cameraOn))
  }

  const createTask = async (event) => {
    event.preventDefault()
    if (!taskState.title.trim()) return
    setTaskState((current) => ({ ...current, sending: true, error: '' }))

    try {
      const response = await fetch(`/api/brain/rooms/${roomId}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: taskState.title.trim(), description: taskState.description.trim(), dueDate: taskState.dueDate || null })
      })

      if (!response.ok) throw new Error('No se pudo crear la tarea.')
      const payload = await response.json()
      setRoom((current) => ({ ...current, tasks: [payload, ...(current?.tasks || [])] }))
      pushLiveItem({ id: `task-live-${Date.now()}`, kind: 'task', label: 'Nueva tarea', text: payload.title })
      setTaskState(initialTaskState)
      setActiveComposer('')
    } catch (err) {
      setTaskState((current) => ({ ...current, sending: false, error: err.message || 'No se pudo crear la tarea.' }))
    }
  }

  const createNote = async (event) => {
    event.preventDefault()
    if (!noteState.title.trim() || !noteState.content.trim()) return
    setNoteState((current) => ({ ...current, sending: true, error: '' }))

    try {
      const response = await fetch(`/api/brain/rooms/${roomId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: noteState.title.trim(), content: noteState.content.trim() })
      })

      if (!response.ok) throw new Error('No se pudo crear la nota.')
      const payload = await response.json()
      setRoom((current) => ({ ...current, notes: [payload, ...(current?.notes || [])] }))
      pushLiveItem({ id: `note-live-${Date.now()}`, kind: 'note', label: 'Nueva nota', text: payload.label })
      setNoteState(initialNoteState)
      setActiveComposer('')
    } catch (err) {
      setNoteState((current) => ({ ...current, sending: false, error: err.message || 'No se pudo crear la nota.' }))
    }
  }

  const inviteParticipant = async (event) => {
    event.preventDefault()
    if (!inviteState.email.trim()) return
    setInviteState((current) => ({ ...current, sending: true, error: '', success: '' }))

    try {
      const response = await fetch(`/api/brain/rooms/${roomId}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: inviteState.email.trim() })
      })

      const payload = await response.json().catch(() => null)
      if (!response.ok) throw new Error(payload?.message || 'No se pudo invitar al participante.')

      const normalizedEmail = inviteState.email.trim().toLowerCase()
      setInvitees((current) => current.filter((item) => String(item.email || '').toLowerCase() !== normalizedEmail))
      setRoom((current) => ({ ...current, invitations: [payload.invitation, ...(current?.invitations || [])] }))
      pushLiveItem({ id: `invite-live-${Date.now()}`, kind: 'invite', label: 'Invitacion enviada', text: inviteState.email.trim() })
      setInviteState({ ...initialInviteState, success: 'Invitacion enviada al usuario.' })
    } catch (err) {
      setInviteState((current) => ({ ...current, sending: false, error: err.message || 'No se pudo invitar al participante.' }))
    }
  }

  const copyJoinLink = async () => {
    if (!joinUrl) return

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(joinUrl)
      } else {
        const textArea = document.createElement('textarea')
        textArea.value = joinUrl
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }

      setInviteState((current) => ({
        ...current,
        error: '',
        success: 'Link de la meet copiado.'
      }))
    } catch (err) {
      setInviteState((current) => ({
        ...current,
        success: '',
        error: 'No se pudo copiar el link de la meet.'
      }))
    }
  }

  const askAi = async (event) => {
    event.preventDefault()
    if (!aiModal.question.trim()) return

    if (typingIntervalRef.current) window.clearInterval(typingIntervalRef.current)
    setAiModal((current) => ({ ...current, sending: true, error: '', visibleAnswer: '', streaming: true }))
    pushLiveItem({ id: `ai-question-${Date.now()}`, kind: 'user', label: 'Pregunta enviada', text: aiModal.question.trim() })

    try {
      const response = await fetch(`/api/brain/rooms/${roomId}/ai/opinion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ question: aiModal.question.trim() })
      })

      if (!response.ok) throw new Error('La IA no pudo responder en este momento.')
      const payload = await response.json()
      setRoom((current) => ({ ...current, aiResponses: [payload, ...(current?.aiResponses || [])] }))

      let index = 0
      typingIntervalRef.current = window.setInterval(() => {
        index += 1
        const nextValue = payload.answer.slice(0, index)
        setAiModal((current) => ({ ...current, visibleAnswer: nextValue, sending: false, streaming: index < payload.answer.length }))

        if (index >= payload.answer.length) {
          window.clearInterval(typingIntervalRef.current)
          typingIntervalRef.current = null
          pushLiveItem({ id: `ai-answer-${Date.now()}`, kind: 'ai', label: 'IA respondio', text: payload.answer })
        }
      }, 16)
    } catch (err) {
      setAiModal((current) => ({ ...current, sending: false, streaming: false, error: err.message || 'Error al consultar IA.' }))
    }
  }

  const generateFeedback = async () => {
    setFeedbackState({ sending: true, error: '' })

    try {
      const response = await fetch(`/api/brain/rooms/${roomId}/ai/feedback`, { method: 'POST', credentials: 'include' })
      if (!response.ok) throw new Error('No se pudo generar el feedback final.')
      const payload = await response.json()
      setRoom((current) => ({ ...current, meetingFeedback: payload }))
      pushLiveItem({ id: `feedback-${Date.now()}`, kind: 'ai', label: 'Feedback generado', text: 'La IA consolido resumen, riesgos y siguientes pasos.' })
      setFeedbackState({ sending: false, error: '' })
    } catch (err) {
      setFeedbackState({ sending: false, error: err.message || 'Error al generar feedback.' })
    }
  }

  const closeAiModal = () => {
    if (typingIntervalRef.current) window.clearInterval(typingIntervalRef.current)
    typingIntervalRef.current = null
    setAiModal(initialAiModalState)
  }

  const appendTaskEmoji = (emoji) => {
    setTaskState((current) => ({ ...current, description: `${current.description}${emoji}` }))
  }

  const appendNoteEmoji = (emoji) => {
    setNoteState((current) => ({ ...current, content: `${current.content}${emoji}` }))
  }

  const appendAiEmoji = (emoji) => {
    setAiModal((current) => ({ ...current, question: `${current.question}${emoji}` }))
  }

  const cycleQuickComposeMode = () => {
    setQuickCompose((current) => {
      const currentIndex = quickComposeModes.indexOf(current.mode)
      const nextMode = quickComposeModes[(currentIndex + 1) % quickComposeModes.length]
      return { ...current, mode: nextMode }
    })
  }

  const submitQuickCompose = async (event) => {
    event?.preventDefault?.()
    const text = quickCompose.text.trim()
    if (!text || quickCompose.sending) return

    setQuickCompose((current) => ({ ...current, sending: true, error: '' }))

    try {
      if (quickCompose.mode === 'task') {
        const response = await fetch(`/api/brain/rooms/${roomId}/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ title: text, description: '', dueDate: null })
        })

        if (!response.ok) throw new Error('No se pudo crear la tarea.')
        const payload = await response.json()
        setRoom((current) => ({ ...current, tasks: [payload, ...(current?.tasks || [])] }))
        pushLiveItem({ id: `quick-task-${Date.now()}`, kind: 'task', label: 'Nueva tarea', text: payload.title })
      } else if (quickCompose.mode === 'note') {
        const response = await fetch(`/api/brain/rooms/${roomId}/notes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ title: 'Nota rapida', content: text })
        })

        if (!response.ok) throw new Error('No se pudo crear la nota.')
        const payload = await response.json()
        setRoom((current) => ({ ...current, notes: [payload, ...(current?.notes || [])] }))
        pushLiveItem({ id: `quick-note-${Date.now()}`, kind: 'note', label: 'Nueva nota', text: payload.content || payload.label })
      } else if (quickCompose.mode === 'chat') {
        const clientMessageId = buildClientMessageId()
        const response = await fetch(`/api/brain/rooms/${roomId}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ message: text, clientMessageId })
        })

        if (!response.ok) throw new Error('No se pudo enviar el mensaje.')
        const payload = await response.json()
        setRoom((current) => current ? ({ ...current, timeline: mergeTimelineItems(current.timeline || [], [payload]) }) : current)
        pushLiveItem({ id: payload.id, kind: 'user', label: payload.author || 'Nuevo mensaje', text: `${payload.author}: ${payload.message}` })
      } else {
        setAiModal({ ...initialAiModalState, open: true, question: text })
      }

      setQuickCompose(initialQuickComposeState)
    } catch (err) {
      setQuickCompose((current) => ({ ...current, sending: false, error: err.message || 'No se pudo enviar el mensaje rapido.' }))
    }
  }

  const joinRoomAsGuest = async (event) => {
    event.preventDefault()
    const name = guestState.name.trim()
    if (!name) {
      setGuestState((current) => ({ ...current, error: 'Escribe tu nombre para entrar.' }))
      return
    }

    setGuestState((current) => ({ ...current, joining: true, error: '' }))

    try {
      const ipOverride = getLocalhostGuestIpOverride()
      const response = await fetch(`/api/brain/public/rooms/${roomId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, ipOverride })
      })

      const payload = await response.json().catch(() => null)
      if (!response.ok) throw new Error(payload?.message || 'No fue posible entrar a la room.')

      await checkAuth()
      await loadAuthenticatedRoomData()
      setGuestState((current) => ({ ...current, joining: false, error: '' }))
    } catch (err) {
      setGuestState((current) => ({ ...current, joining: false, error: err.message || 'No fue posible entrar a la room.' }))
    }
  }

  if (loading) return <div className="min-h-[70vh] flex items-center justify-center text-slate-600"><Loader2 className="w-6 h-6 animate-spin mr-2" />Cargando room...</div>
  if (error) return <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-rose-700">{error}</div>
  if (!hasRoomAccess) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(251,191,36,0.18),_transparent_28%),linear-gradient(180deg,#0f172a_0%,#020617_100%)] px-4 py-8 text-white sm:px-6 lg:px-8">
        <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-5xl items-center justify-center">
          <div className="grid w-full overflow-hidden rounded-[32px] border border-white/10 bg-slate-950/60 shadow-[0_30px_120px_rgba(2,6,23,0.45)] backdrop-blur-xl lg:grid-cols-[1.1fr_0.9fr]">
            <div className="p-7 sm:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300">BrainEmprendedor Meet</p>
              <h1 className="mt-4 text-3xl font-semibold text-white sm:text-4xl">{roomPreview?.name || 'Entrar a la room'}</h1>
              <p className="mt-4 max-w-xl text-sm leading-7 text-slate-300 sm:text-base">
                {roomPreview?.description || 'Ingresa sin friccion, escribe tu nombre y entra a la conversacion en segundos.'}
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <div className="rounded-3xl border border-white/10 bg-white/5 px-5 py-4">
                  <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Objetivo</p>
                  <p className="mt-2 text-sm text-slate-200">{roomPreview?.objective || 'Salir con acuerdos claros y siguientes pasos visibles.'}</p>
                </div>
                <div className="rounded-3xl border border-white/10 bg-white/5 px-5 py-4">
                  <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Capacidad</p>
                  <p className="mt-2 text-sm text-slate-200">{roomPreview?.participantsCount || 0} de {roomPreview?.maxParticipants || 5} personas dentro de la room.</p>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 bg-white/[0.03] p-7 sm:p-10 lg:border-l lg:border-t-0">
              <div className="rounded-[28px] border border-white/10 bg-slate-950/70 p-6 shadow-2xl">
                <h2 className="text-2xl font-semibold text-white">Escribe tu nombre para entrar</h2>
                <p className="mt-2 text-sm text-slate-400">
                  {isAuthenticated ? 'Aunque ya tengas sesion, debes confirmar tu nombre para sumarte a esta room.' : 'No necesitas registrarte ni iniciar sesion para unirte a esta meet.'}
                </p>

                <form onSubmit={joinRoomAsGuest} className="mt-6 grid gap-4">
                  <input
                    className="w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="Ejemplo: Felipe Acuarela"
                    value={guestState.name}
                    onChange={(event) => setGuestState((current) => ({ ...current, name: event.target.value, error: '' }))}
                    autoFocus
                  />
                  {guestState.error && <div className="rounded-2xl border border-rose-300/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{guestState.error}</div>}
                  <button type="submit" disabled={guestState.joining} className="inline-flex items-center justify-center rounded-2xl bg-amber-400 px-4 py-3 text-sm font-semibold text-slate-950 hover:bg-amber-300 disabled:opacity-60">
                    {guestState.joining ? 'Entrando...' : 'Entrar a la room'}
                  </button>
                </form>

                <p className="mt-4 text-xs leading-6 text-slate-500">
                  Al entrar, quedaras registrado como participante de esta room y tu nombre sera visible para los demas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 overflow-hidden bg-slate-950 md:static md:bg-transparent md:overflow-visible md:space-y-3 md:pb-20 lg:pb-4">
      <style>{liveStyleKeyframes}</style>
      <section className="h-full space-y-0 md:h-auto md:space-y-4">
        <div className="h-full space-y-0 md:h-auto md:space-y-4">
          <article className="relative h-full overflow-hidden rounded-none bg-slate-950 p-0 text-slate-100 shadow-sm md:h-auto md:min-h-0 md:rounded-[28px] md:p-5">
            <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-r from-amber-400/10 via-transparent to-rose-400/10" />
            <div className="brain-room-stage-shell relative grid h-full overflow-hidden rounded-none border-x-0 border-y-0 border-slate-800 bg-[radial-gradient(circle_at_top,_rgba(251,191,36,0.10),_transparent_28%),linear-gradient(180deg,#0f172a_0%,#020617_100%)] px-0 py-0 md:block md:h-auto md:min-h-0 md:rounded-[24px] md:border md:px-4 md:pb-24 md:pt-4" style={mobileStageInlineStyle}>
              <div className="brain-room-mobile-stage relative h-full min-h-0 bg-slate-950/30 md:h-[clamp(420px,70dvh,760px)]">
                <div className="brain-live-scroll brain-room-stage-feed absolute inset-y-0 left-0 z-20 hidden overflow-y-auto rounded-[22px] border border-white/8 bg-slate-950/24 p-3 shadow-[0_18px_50px_rgba(2,6,23,0.24)] backdrop-blur-md md:block">
                  <MinimalFeedOverlay items={liveFeed} participants={participants} currentUserName={currentUserName} fullHeight />
                </div>

                <div className="brain-room-stage-main flex h-full items-center justify-center">
                  <div ref={stageViewportRef} className="brain-room-stage-main-inner relative z-0 h-full w-full max-w-[min(100%,940px)] overflow-hidden rounded-none border-0 bg-slate-950 shadow-none md:rounded-[24px] md:border md:border-white/8 md:bg-slate-900 md:shadow-[0_24px_90px_rgba(2,6,23,0.45)]">
                    {hasParticipantGrid ? (
                      <div
                        className="grid h-full w-full bg-slate-950 p-1.5 sm:gap-3 sm:p-3 md:gap-2 md:p-2"
                        style={stageLayout.gridStyle}
                      >
                        {participantTiles.map((participant, index) => {
                          const participantId = normalizeUserId(participant.id)
                          const isLocalTile = participantId === currentUserId
                          const participantMedia = getParticipantMedia(participantId)

                          return (
                            <Participant
                              key={participantId}
                              participant={participant}
                              stream={null}
                              videoTrack={participantMedia.videoTrack || null}
                              audioTrack={participantMedia.audioTrack || null}
                              isLocal={isLocalTile}
                              highlighted={participantId === activeSpeakerId}
                              tileStyle={stageLayout.getItemStyle(index)}
                              onStreamBound={({ participantId: boundParticipantId, kind, trackSignature }) => {
                                brainRtcLog('participant_stream_bound', {
                                  participantId: normalizeUserId(boundParticipantId),
                                  kind,
                                  trackSignature
                                })
                              }}
                            />
                          )
                        })}
                      </div>
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center bg-slate-950">
                        <div className={`flex h-36 w-36 items-center justify-center rounded-full text-4xl font-bold md:h-44 md:w-44 ${normalizeUserId(currentSpeaker?.id) === activeSpeakerId ? 'bg-amber-300 text-slate-950 shadow-[0_0_0_12px_rgba(251,191,36,0.12)]' : 'bg-slate-800 text-slate-100'}`}>
                          {currentSpeaker?.initials || 'RD'}
                        </div>
                        <p className="mt-5 text-xl font-semibold text-white md:text-2xl">{currentSpeaker?.name || 'Room digital'}</p>
                      </div>
                    )}

                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-950/75 to-transparent md:h-24" />
                    {!hasParticipantGrid && (
                      <div className="absolute bottom-2 left-3 px-0 py-0 text-[11px] text-white/88 md:bottom-3 md:left-3 md:rounded-full md:border md:border-white/10 md:bg-slate-950/38 md:px-2.5 md:py-1 md:backdrop-blur-md sm:bottom-4 sm:left-4 sm:px-3 sm:text-xs">
                        {currentSpeaker?.name || room.name}
                      </div>
                    )}
                  </div>
                </div>

                <div className="absolute right-2 top-1/2 z-20 hidden -translate-y-1/2 flex-col gap-2 sm:right-3 sm:gap-3 md:flex">
                  {participants.slice(0, maxParticipants).map((participant) => {
                    const participantId = normalizeUserId(participant?.id)
                    const isLocal = participantId === currentUserId
                    const isSpeaking = participantId === activeSpeakerId

                    return (
                      <button
                        key={participantId}
                        type="button"
                        title={isLocal ? 'Tu burbuja' : participant.name}
                        onClick={() => setActiveSpeakerId(participantId)}
                        className={`group relative flex h-10 w-10 items-center justify-center rounded-full border backdrop-blur-md transition sm:h-12 sm:w-12 md:h-14 md:w-14 ${isSpeaking ? 'border-amber-300 bg-amber-300/18 shadow-[0_0_0_8px_rgba(251,191,36,0.10)]' : 'border-white/10 bg-slate-950/28 hover:bg-slate-950/42'}`}
                      >
                        <span className="text-xs font-semibold text-white sm:text-sm md:text-base">{participant.initials}</span>
                        {isSpeaking && <span className="absolute bottom-1 right-1 h-3 w-3 rounded-full border border-slate-950 bg-emerald-400" />}
                        {isLocal && mediaState.cameraOn && <span className="absolute left-1 top-1 h-2.5 w-2.5 rounded-full bg-sky-400" />}
                        <span className="pointer-events-none absolute right-[calc(100%+10px)] top-1/2 hidden -translate-y-1/2 rounded-full border border-white/10 bg-slate-950/90 px-3 py-1 text-[11px] text-white shadow-xl group-hover:block">
                          {isLocal ? 'Tu burbuja' : participant.name}
                        </span>
                      </button>
                    )
                  })}
                </div>

                <div className="absolute bottom-4 right-2 z-20 hidden md:flex sm:right-3">
                  <button
                    type="button"
                    title="Invitar personas"
                    aria-label="Invitar personas"
                    onClick={() => {
                      setInviteState((current) => ({ ...current, error: '', success: '' }))
                      setActiveComposer((current) => current === 'invite' ? '' : 'invite')
                    }}
                    className="group relative inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-slate-950/40 text-white shadow-[0_18px_40px_rgba(2,6,23,0.35)] backdrop-blur-md transition hover:-translate-y-0.5 hover:bg-slate-950/55"
                  >
                    <Plus className="h-5 w-5" />
                    <span className="pointer-events-none absolute right-[calc(100%+10px)] top-1/2 hidden -translate-y-1/2 rounded-full border border-white/10 bg-slate-950/95 px-3 py-1 text-[11px] text-white shadow-xl group-hover:block">
                      Invitar personas
                    </span>
                  </button>
                </div>
              </div>

              <div className="brain-room-stage-title absolute inset-x-0 top-0 flex items-start justify-between p-2.5 sm:p-4">
                <div className="rounded-full border border-white/10 bg-slate-950/30 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/85 backdrop-blur-md sm:px-3 sm:text-[11px] sm:tracking-[0.24em]">
                  {room.name}
                </div>
                <button
                  type="button"
                  aria-label="Menu de la room"
                  onClick={() => setMobileMenuOpen(true)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-slate-950/82 text-white shadow-[0_18px_40px_rgba(2,6,23,0.28)] backdrop-blur-md md:hidden"
                >
                  <Menu className="h-4 w-4" />
                </button>
              </div>

              <div className="brain-room-mobile-feed h-full min-h-0 rounded-none border-x-0 border-y border-white/10 px-2 py-1 backdrop-blur-md md:hidden">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/45">Conversacion en vivo</p>
                  <span className="text-[10px] text-white/35">{liveFeed.length} items</span>
                </div>
                <div className="brain-live-scroll h-[calc(100%-1.35rem)] overflow-y-auto pr-1">
                  <MinimalFeedOverlay items={liveFeed} participants={participants} currentUserName={currentUserName} />
                </div>
              </div>

              <form onSubmit={submitQuickCompose} className="brain-room-stage-compose relative z-20 flex h-full justify-center px-0 md:absolute md:inset-x-0 md:bottom-3 md:h-auto md:px-3 sm:bottom-4 sm:px-4">
                <div className="brain-room-stage-compose-card flex h-full w-full max-w-[830px] items-center gap-1 rounded-none border-x-0 border-y-0 border-t border-white/10 bg-slate-950/82 px-1.5 py-1 shadow-none backdrop-blur-xl md:gap-1.5 md:rounded-full md:border md:px-2 md:py-1.5 md:shadow-[0_18px_40px_rgba(2,6,23,0.35)] sm:gap-2 sm:px-3 sm:py-2">
                  <div className="order-1 flex items-center gap-1.5 pr-1 sm:gap-2 sm:pr-2">
                    <IconCircleButton label="Microfono" icon={Mic} subtle />
                    <IconCircleButton
                      label={mediaState.broadcasting || mediaState.cameraOn ? 'Detener transmision' : 'Compartir camara'}
                      icon={mediaState.broadcasting || mediaState.cameraOn ? CameraOff : MonitorUp}
                      subtle
                      onClick={toggleBroadcast}
                    />
                  </div>
                  <div className="order-2 hidden h-6 w-px bg-white/10 md:block sm:h-8" />
                  <input
                    className="order-3 min-w-0 flex-1 bg-transparent px-1 text-[10px] text-white placeholder:text-white/45 focus:outline-none md:px-2 md:text-xs sm:px-3 sm:text-sm"
                    placeholder={quickCompose.mode === 'task' ? 'Escribe una tarea rapida' : quickCompose.mode === 'note' ? 'Escribe una nota rapida' : quickCompose.mode === 'chat' ? 'Escribe un mensaje a la room' : 'Pregunta rapido a la IA'}
                    value={quickCompose.text}
                    onChange={(event) => setQuickCompose((current) => ({ ...current, text: event.target.value, error: '' }))}
                  />
                  <div className="order-4 hidden items-center gap-1 md:flex">
                    <QuickModeButton icon={Send} label="Chat" active={quickCompose.mode === 'chat'} onClick={() => setQuickCompose((current) => ({ ...current, mode: 'chat' }))} />
                    <QuickModeButton icon={CheckCircle2} label="Tarea" active={quickCompose.mode === 'task'} onClick={() => setQuickCompose((current) => ({ ...current, mode: 'task' }))} />
                    <QuickModeButton icon={MessageSquareText} label="Nota" active={quickCompose.mode === 'note'} onClick={() => setQuickCompose((current) => ({ ...current, mode: 'note' }))} />
                    <QuickModeButton icon={Brain} label="IA" active={quickCompose.mode === 'ai'} onClick={() => setQuickCompose((current) => ({ ...current, mode: 'ai' }))} />
                  </div>
                  <QuickModeButton
                    icon={quickCompose.mode === 'task' ? CheckCircle2 : quickCompose.mode === 'note' ? MessageSquareText : quickCompose.mode === 'ai' ? Brain : Send}
                    label={`Modo ${quickCompose.mode}`}
                    active
                    onClick={cycleQuickComposeMode}
                    mobileOnly
                  />
                  <button type="submit" disabled={!quickCompose.text.trim() || quickCompose.sending} className="order-5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-slate-950 transition hover:bg-slate-200 disabled:opacity-45">
                    <Send className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                </div>
              </form>

              {quickCompose.error && <div className="absolute bottom-14 left-1/2 hidden -translate-x-1/2 rounded-full border border-rose-300/20 bg-rose-500/10 px-3 py-1.5 text-[11px] text-rose-100 backdrop-blur-md md:block sm:bottom-16 sm:text-xs">{quickCompose.error}</div>}

              <div className="absolute bottom-4 right-4 hidden flex-col gap-3">
                <QuickActionButton label="Tarea" icon={CheckCircle2} onClick={() => setActiveComposer((current) => current === 'task' ? '' : 'task')} />
                <QuickActionButton label="Nota" icon={MessageSquareText} onClick={() => setActiveComposer((current) => current === 'note' ? '' : 'note')} />
                <QuickActionButton label="IA" icon={Brain} onClick={() => setAiModal((current) => ({ ...current, open: true }))} primary />
              </div>

              {mediaState.error && <div className="absolute left-4 top-16 rounded-full border border-rose-300/20 bg-rose-500/10 px-3 py-1.5 text-xs text-rose-100 backdrop-blur-md">{mediaState.error}</div>}

              <div className="pointer-events-none absolute inset-x-0 bottom-0 hidden h-20 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent sm:h-24 md:block" />

              {activeComposer === 'task' && (
                <OverlayComposer title="Crear tarea al instante" subtitle="No te salgas de la conversacion. Digita y guarda." onClose={() => setActiveComposer('')}>
                  <form onSubmit={createTask} className="grid gap-3">
                    <input className="w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400" placeholder="Ejemplo: confirmar propuesta comercial" value={taskState.title} onChange={(event) => setTaskState((current) => ({ ...current, title: event.target.value }))} required />
                    <div className="rounded-[24px] border border-white/15 bg-white/5 p-3 backdrop-blur">
                      <textarea className="w-full resize-none bg-transparent px-1 py-1 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none" rows={3} placeholder="Descripcion opcional" value={taskState.description} onChange={(event) => setTaskState((current) => ({ ...current, description: event.target.value }))} />
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <EmojiPicker onSelect={appendTaskEmoji} />
                        <span className="text-xs text-slate-500">Puedes agregar emoticones al vuelo.</span>
                      </div>
                    </div>
                    <input className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400" type="date" value={taskState.dueDate} onChange={(event) => setTaskState((current) => ({ ...current, dueDate: event.target.value }))} />
                    {taskState.error && <div className="rounded-2xl border border-rose-300/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{taskState.error}</div>}
                    <button type="submit" disabled={taskState.sending} className="rounded-2xl bg-emerald-500 px-4 py-3 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-60">{taskState.sending ? 'Guardando...' : 'Crear tarea'}</button>
                  </form>
                </OverlayComposer>
              )}

              {activeComposer === 'note' && (
                <OverlayComposer title="Crear nota rapida" subtitle="Captura el acuerdo sin abandonar la reunion." onClose={() => setActiveComposer('')}>
                  <form onSubmit={createNote} className="grid gap-3">
                    <input className="w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-400" placeholder="Titulo de la nota" value={noteState.title} onChange={(event) => setNoteState((current) => ({ ...current, title: event.target.value }))} required />
                    <div className="rounded-[24px] border border-white/15 bg-white/5 p-3 backdrop-blur">
                      <textarea className="w-full resize-none bg-transparent px-1 py-1 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none" rows={4} placeholder="Escribe la nota..." value={noteState.content} onChange={(event) => setNoteState((current) => ({ ...current, content: event.target.value }))} required />
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <EmojiPicker onSelect={appendNoteEmoji} />
                        <span className="text-xs text-slate-500">Notas rapidas con emoji incluido.</span>
                      </div>
                    </div>
                    {noteState.error && <div className="rounded-2xl border border-rose-300/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{noteState.error}</div>}
                    <button type="submit" disabled={noteState.sending} className="rounded-2xl bg-sky-500 px-4 py-3 text-sm font-semibold text-white hover:bg-sky-400 disabled:opacity-60">{noteState.sending ? 'Guardando...' : 'Crear nota'}</button>
                  </form>
                </OverlayComposer>
              )}

              {activeComposer === 'invite' && (
                <OverlayComposer title="Invitar personas a la meet" subtitle="Invita por correo o copia el link unico de esta room." onClose={() => setActiveComposer('')}>
                  <form onSubmit={inviteParticipant} className="grid gap-3">
                    <input
                      className="w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400"
                      type="email"
                      placeholder="correo@ejemplo.com"
                      value={inviteState.email}
                      onChange={(event) => setInviteState((current) => ({ ...current, email: event.target.value, error: '', success: '' }))}
                      required
                    />
                    <div className="rounded-[20px] border border-white/12 bg-white/[0.04] p-3 text-sm text-slate-300 backdrop-blur">
                      <div className="flex items-center gap-2 text-amber-300">
                        <Link2 className="h-3.5 w-3.5" />
                        <span className="text-xs font-semibold uppercase tracking-[0.18em]">Link unico de la meet</span>
                      </div>
                      <p className="mt-2 break-all rounded-xl border border-white/10 bg-slate-950/60 px-3 py-2 text-[11px] leading-5 text-slate-200">
                        {joinUrl}
                      </p>
                      <button
                        type="button"
                        onClick={copyJoinLink}
                        className="mt-2 inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-xs font-semibold text-white hover:bg-white/10"
                      >
                        <Copy className="h-3.5 w-3.5" />
                        Copiar link
                      </button>
                    </div>
                    {invitees.length > 0 && (
                      <p className="text-xs text-slate-500">
                        Puedes invitar usuarios registrados por correo. Si prefieres, comparte el link para que entren a esta room.
                      </p>
                    )}
                    {(inviteState.error || inviteState.success) && (
                      <div className={`rounded-2xl px-4 py-3 text-sm ${inviteState.error ? 'border border-rose-300/30 bg-rose-500/10 text-rose-200' : 'border border-emerald-300/30 bg-emerald-500/10 text-emerald-100'}`}>
                        {inviteState.error || inviteState.success}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-3">
                      <button type="submit" disabled={inviteState.sending} className="rounded-2xl bg-amber-400 px-4 py-3 text-sm font-semibold text-slate-950 hover:bg-amber-300 disabled:opacity-60">
                        {inviteState.sending ? 'Enviando...' : 'Enviar invitacion'}
                      </button>
                      <button type="button" onClick={() => setActiveComposer('')} className="rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10">
                        Cerrar
                      </button>
                    </div>
                  </form>
                </OverlayComposer>
              )}
            </div>
          </article>

          {room.meetingFeedback && (
          <article className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Feedback final de la reunion</h2>
                <p className="mt-1 text-sm text-slate-500">Genera resumen, riesgos y siguientes pasos al cierre.</p>
              </div>
              <button type="button" onClick={generateFeedback} disabled={feedbackState.sending} className="rounded-xl bg-amber-400 px-4 py-3 text-sm font-semibold text-slate-950 hover:bg-amber-300 disabled:opacity-60">{feedbackState.sending ? 'Generando...' : 'Generar feedback'}</button>
            </div>

            {feedbackState.error && <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{feedbackState.error}</div>}

              <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <FeedbackCard title="Resumen" items={[room.meetingFeedback.summary]} />
                <FeedbackCard title="Decisiones" items={room.meetingFeedback.decisions} />
                <FeedbackCard title="Riesgos" items={room.meetingFeedback.risks} />
                <FeedbackCard title="Siguientes pasos" items={room.meetingFeedback.nextSteps} />
              </div>
          </article>
          )}
        </div>
      </section>

      {aiModal.open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur sm:items-center sm:p-6">
          <div className="w-full max-w-5xl overflow-hidden rounded-t-[28px] border border-white/10 bg-slate-950/88 shadow-2xl backdrop-blur-xl sm:rounded-[28px]">
            <div className="grid lg:grid-cols-[1.2fr_0.8fr]">
              <div className="p-6 md:p-8">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">Preguntar a la IA</p>
                    <h2 className="mt-1 text-2xl font-semibold text-white">Escribe la pregunta y mira la respuesta aparecer</h2>
                  </div>
                  <button type="button" onClick={closeAiModal} className="rounded-full border border-white/15 p-2 text-slate-400 hover:bg-white/10">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={askAi} className="mt-6 space-y-4">
                  <div className="rounded-[24px] border border-white/15 bg-white/5 p-4 backdrop-blur">
                    <textarea className="w-full resize-none bg-transparent px-1 py-1 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none" rows={5} value={aiModal.question} onChange={(event) => setAiModal((current) => ({ ...current, question: event.target.value, error: '' }))} placeholder="Ejemplo: cual es el mayor riesgo de ejecucion, quien deberia encargarse y que acciones debemos tomar esta semana?" />
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <EmojiPicker onSelect={appendAiEmoji} />
                      <span className="text-xs text-slate-400">Pregunta con emoticones si quieres mantener el tono live.</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-slate-400">Escribe normal. La IA respondera dentro de esta misma experiencia.</p>
                    <button type="submit" disabled={aiModal.sending || !aiModal.question.trim()} className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
                      <Send className="w-4 h-4" />
                      {aiModal.sending ? 'Consultando...' : 'Preguntar'}
                    </button>
                  </div>
                  {aiModal.error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{aiModal.error}</div>}
                </form>

                <div className="mt-6 rounded-[24px] border border-white/15 bg-transparent p-5 text-slate-100">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-amber-300">
                      <Brain className="w-4 h-4" />
                      <span className="text-sm font-semibold">Respuesta en vivo</span>
                    </div>
                    {aiModal.streaming && <span className="inline-flex items-center gap-2 rounded-full bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-200">La IA esta escribiendo<span className="flex gap-1"><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-amber-300 [animation-delay:-0.3s]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-amber-300 [animation-delay:-0.15s]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-amber-300" /></span></span>}
                  </div>
                  <p className="mt-4 min-h-[160px] whitespace-pre-wrap text-sm leading-7 text-slate-200">{aiModal.visibleAnswer || 'Todavia no hay respuesta. Haz una pregunta para ver la escritura en vivo de la IA.'}</p>
                </div>
              </div>
              <div className="border-t border-white/10 bg-transparent p-6 md:p-8 lg:border-l lg:border-t-0">
                <h3 className="text-lg font-semibold text-white">Feed tipo live</h3>
                <p className="mt-1 text-sm text-slate-400">Mensajes flotando desde la esquina izquierda, sin panel pesado.</p>
                <div className="relative mt-5 min-h-[340px] overflow-hidden rounded-[24px] border border-white/10 bg-white/[0.03]">
                  <LiveFeedOverlay items={aiModal.question.trim() ? [{ id: 'question-preview', kind: 'user', label: 'Tu pregunta', text: aiModal.question }, ...liveFeed] : liveFeed} modal />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-end bg-slate-950/62 p-3 backdrop-blur-sm md:hidden">
          <div className="w-full max-w-xs overflow-hidden rounded-[24px] border border-white/10 bg-slate-950/92 shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/45">Menu de la room</p>
                <p className="mt-1 text-sm text-white/82">Acciones rapidas sin salir del video chat</p>
              </div>
              <button
                type="button"
                aria-label="Cerrar menu"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/75"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid gap-2 p-3">
              <Link
                to="/brain-emprendedor/dashboard"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/88"
              >
                <Brain className="h-4 w-4 text-amber-300" />
                <span>Salir de la room</span>
              </Link>
              <button
                type="button"
                onClick={() => {
                  setActiveComposer('task')
                  setMobileMenuOpen(false)
                }}
                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/88"
              >
                <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                <span>Crear tarea</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveComposer('note')
                  setMobileMenuOpen(false)
                }}
                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/88"
              >
                <MessageSquareText className="h-4 w-4 text-sky-300" />
                <span>Crear nota</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setAiModal((current) => ({ ...current, open: true }))
                  setMobileMenuOpen(false)
                }}
                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/88"
              >
                <Send className="h-4 w-4 text-violet-300" />
                <span>Abrir IA</span>
              </button>
            </div>

            <div className="border-t border-white/10 px-3 py-3">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="flex w-full items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/82"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Volver al video chat</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {isBrainRtcDebugEnabled && (
        <div className="fixed bottom-4 left-4 z-40 w-[380px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-amber-300/20 bg-slate-950/88 shadow-2xl backdrop-blur">
          <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-200/80">RTC Debug</p>
              <p className="text-[11px] text-white/45">{rtcDebugFeed.length} eventos recientes</p>
            </div>
            <button
              type="button"
              onClick={() => setRtcDebugFeed([])}
              className="rounded-full border border-white/10 px-2 py-1 text-[10px] text-white/60 hover:bg-white/5"
            >
              Limpiar
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto px-3 py-2">
            {rtcDebugFeed.length === 0 ? (
              <p className="text-xs text-white/40">Sin eventos todavia.</p>
            ) : (
              <div className="space-y-2">
                {rtcDebugFeed.map((item) => (
                  <div key={item.id} className="rounded-xl border border-white/10 bg-white/[0.03] px-2.5 py-2">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[11px] font-semibold text-amber-100">{item.event}</span>
                      <span className="text-[10px] text-white/35">{item.timestamp}</span>
                    </div>
                    <p className="mt-1 break-all font-mono text-[10px] leading-4 text-white/55">{item.details}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function QuickActionButton({ label, icon: Icon, onClick, primary = false, compact = false }) {
  return (
    <button type="button" title={label} aria-label={label} onClick={onClick} className={`group pointer-events-auto relative inline-flex items-center justify-center rounded-full shadow-lg transition-transform hover:-translate-y-0.5 ${compact ? 'h-8 w-8 sm:h-10 sm:w-10 lg:h-11 lg:w-11' : 'h-14 w-14'} ${primary ? 'bg-amber-400 text-slate-950 hover:bg-amber-300' : 'bg-white text-slate-900 hover:bg-slate-100'}`}>
      <Icon className={compact ? 'h-3.5 w-3.5 sm:h-4 sm:w-4' : 'w-5 h-5'} />
      <span className="pointer-events-none absolute right-[calc(100%+12px)] top-1/2 hidden -translate-y-1/2 rounded-full border border-white/10 bg-slate-950/95 px-3 py-1 text-xs font-medium text-white shadow-xl group-hover:block">
        {label}
      </span>
    </button>
  )
}

function QuickModeButton({ icon: Icon, label, active, onClick, mobileOnly = false }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={`${mobileOnly ? 'inline-flex md:hidden' : 'inline-flex'} h-7 w-7 items-center justify-center rounded-full border transition sm:h-8 sm:w-8 ${active ? 'border-amber-300/50 bg-amber-300/16 text-amber-200' : 'border-white/10 bg-white/5 text-white/75 hover:bg-white/10'}`}
    >
      <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
    </button>
  )
}

function OverlayComposer({ title, subtitle, children, onClose }) {
  return (
    <div className="absolute inset-x-4 bottom-4 z-20 rounded-[28px] border border-white/15 bg-slate-950/60 p-5 shadow-2xl backdrop-blur-xl">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <p className="mt-1 text-xs text-slate-400">{subtitle}</p>
        </div>
        <button type="button" onClick={onClose} className="rounded-full border border-slate-700 p-2 text-slate-400 hover:bg-slate-800">
          <X className="w-4 h-4" />
        </button>
      </div>
      {children}
    </div>
  )
}

function IconCircleButton({ label, icon: Icon, onClick, subtle = false, disabled = false }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={`group relative inline-flex h-8 w-8 items-center justify-center rounded-full border text-slate-300 transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40 sm:h-9 sm:w-9 md:h-10 md:w-10 ${subtle ? 'border-white/10 bg-slate-950/28 text-white backdrop-blur-md hover:bg-slate-950/42' : 'border-slate-800 bg-slate-900 hover:border-slate-700 hover:bg-slate-800'}`}
    >
      <Icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${subtle ? 'text-white' : 'text-amber-300'}`} />
      <span className="pointer-events-none absolute top-[calc(100%+10px)] hidden rounded-full border border-white/10 bg-slate-950/95 px-3 py-1 text-[11px] font-medium text-white shadow-xl group-hover:block">
        {label}
      </span>
    </button>
  )
}

function EmojiPicker({ onSelect }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative">
      <button type="button" title="Emoticones" aria-label="Emoticones" onClick={() => setOpen((current) => !current)} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-slate-200 hover:bg-white/10">
        <SmilePlus className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute bottom-[calc(100%+12px)] left-0 z-30 w-56 rounded-3xl border border-white/15 bg-slate-950/95 p-3 shadow-2xl backdrop-blur-xl">
          <div className="grid grid-cols-4 gap-2">
            {quickEmojis.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => {
                  onSelect(emoji)
                  setOpen(false)
                }}
                className="rounded-2xl bg-white/5 px-2 py-2 text-lg hover:bg-white/10"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function MinimalFeedOverlay({ items, participants, currentUserName, fullHeight = false }) {
  return (
    <div className={`brain-live-scroll pointer-events-auto flex flex-col justify-end gap-2 pr-1 ${fullHeight ? 'h-full overflow-y-auto overflow-x-hidden' : 'max-h-28 overflow-y-auto overflow-x-hidden sm:max-h-36'}`}>
      {items.slice(0, 8).reverse().map((item) => {
        const meta = getLiveFeedParticipantMeta(item, participants, currentUserName)

        return (
          <div key={item.id} className="flex items-start gap-2">
            <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold shadow-[0_10px_24px_rgba(15,23,42,0.22)] sm:h-8 sm:w-8 sm:text-[11px] ${meta.accent}`}>
              {meta.initials}
            </div>
            <div className="min-w-0 flex-1 rounded-[22px] border border-white/8 bg-slate-950/18 px-2.5 py-1.5 text-white shadow-[0_10px_30px_rgba(15,23,42,0.22)] backdrop-blur-md sm:px-3 sm:py-2">
              <p className="break-words text-[11px] leading-4 text-white/88 sm:text-xs sm:leading-5">{item.text}</p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function LiveFeedOverlay({ items, modal = false }) {
  return (
    <div className={`pointer-events-none absolute bottom-6 left-4 z-10 flex w-[min(320px,calc(100%-7rem))] flex-col justify-end gap-3 ${modal ? 'right-4 top-4 bottom-4 w-auto' : ''}`}>
      {items.slice(0, 6).reverse().map((item, index) => (
        <div
          key={item.id}
          className="rounded-[22px] border border-white/10 bg-slate-950/18 px-4 py-3 text-white shadow-[0_16px_50px_rgba(15,23,42,0.35)] backdrop-blur-md"
          style={{ animation: `live-slide-up 420ms ease-out`, animationDelay: `${index * 70}ms` }}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-amber-200/90">{item.label}</p>
          <p className="mt-2 text-sm leading-5 text-slate-100">{item.text}</p>
        </div>
      ))}
    </div>
  )
}

function getOptimalGrid(participantCount, containerRatio = DEFAULT_STAGE_RATIO, targetTileRatio = DEFAULT_STAGE_RATIO) {
  if (participantCount <= 0) {
    return {
      columns: 1,
      rows: 1,
      virtualColumns: 1
    }
  }

  const normalizedContainerRatio = Number.isFinite(containerRatio) && containerRatio > 0
    ? containerRatio
    : DEFAULT_STAGE_RATIO

  let bestLayout = null

  for (let columns = 1; columns <= participantCount; columns += 1) {
    const rows = Math.ceil(participantCount / columns)
    const cellWidth = normalizedContainerRatio / columns
    const cellHeight = 1 / rows
    const tileWidth = Math.min(cellWidth, cellHeight * targetTileRatio)
    const tileHeight = tileWidth / targetTileRatio
    const usedArea = tileWidth * tileHeight * participantCount
    const emptySlots = (columns * rows) - participantCount
    const shapeDelta = Math.abs(columns - rows)
    const aspectDelta = Math.abs((cellWidth / cellHeight) - targetTileRatio)

    const candidate = {
      columns,
      rows,
      usedArea,
      emptySlots,
      shapeDelta,
      aspectDelta
    }

    if (!bestLayout ||
      candidate.usedArea > bestLayout.usedArea + 0.0001 ||
      (Math.abs(candidate.usedArea - bestLayout.usedArea) <= 0.0001 && candidate.emptySlots < bestLayout.emptySlots) ||
      (Math.abs(candidate.usedArea - bestLayout.usedArea) <= 0.0001 && candidate.emptySlots === bestLayout.emptySlots && candidate.shapeDelta < bestLayout.shapeDelta) ||
      (Math.abs(candidate.usedArea - bestLayout.usedArea) <= 0.0001 && candidate.emptySlots === bestLayout.emptySlots && candidate.shapeDelta === bestLayout.shapeDelta && candidate.aspectDelta < bestLayout.aspectDelta)
    ) {
      bestLayout = candidate
    }
  }

  const remainingItems = participantCount % bestLayout.columns
  const hasPartialLastRow = remainingItems > 0
  const virtualColumns = hasPartialLastRow
    ? bestLayout.columns * remainingItems
    : bestLayout.columns

  return {
    columns: bestLayout.columns,
    rows: bestLayout.rows,
    virtualColumns
  }
}

function buildParticipantGridLayout(participantCount, containerRatio) {
  const optimalGrid = getOptimalGrid(participantCount, containerRatio)
  const { columns, rows, virtualColumns } = optimalGrid
  const remainingItems = participantCount % columns
  const hasPartialLastRow = remainingItems > 0
  const lastRowCount = hasPartialLastRow ? remainingItems : columns
  const fullRowSpan = virtualColumns / columns
  const lastRowSpan = virtualColumns / lastRowCount
  const fullRowsItemCount = hasPartialLastRow
    ? participantCount - lastRowCount
    : participantCount

  return {
    ...optimalGrid,
    gridStyle: {
      gridTemplateColumns: `repeat(${virtualColumns}, minmax(0, 1fr))`,
      gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
      gridAutoFlow: 'row',
      alignContent: 'stretch'
    },
    getItemStyle: (index) => ({
      gridColumn: `span ${index < fullRowsItemCount ? fullRowSpan : lastRowSpan} / span ${index < fullRowsItemCount ? fullRowSpan : lastRowSpan}`,
      minWidth: 0,
      minHeight: 0
    })
  }
}

function FeedbackCard({ title, items }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <p className="font-semibold text-slate-900">{title}</p>
      <div className="mt-3 space-y-2 text-sm text-slate-600">
        {items.map((item) => <div key={item} className="rounded-xl bg-white px-3 py-3">{item}</div>)}
      </div>
    </div>
  )
}

function buildInitialLiveFeed(room) {
  const feed = []
  if ((room?.tasks || [])[0]) feed.push({ id: 'task-seed', kind: 'task', label: 'Ultima tarea', text: room.tasks[0].title })
  if ((room?.notes || [])[0]) feed.push({ id: 'note-seed', kind: 'note', label: 'Ultima nota', text: room.notes[0].label })
  if ((room?.aiResponses || [])[0]) feed.push({ id: 'ai-seed', kind: 'ai', label: 'Ultima respuesta IA', text: room.aiResponses[0].answer })
  return feed
}

function mergeLiveFeed(currentFeed, seededFeed) {
  const combined = [...(currentFeed || [])]

  for (const item of seededFeed || []) {
    const exists = combined.some((entry) => entry.id === item.id || (entry.kind === item.kind && entry.text === item.text))
    if (!exists) {
      combined.push(item)
    }
  }

  return combined.slice(0, 8)
}

function getLiveFeedParticipantMeta(item, participants, currentUserName) {
  const text = String(item?.text || '')
  const participant = (participants || []).find((entry) => text.includes(entry.name))

  if (participant) {
    return {
      initials: participant.initials,
      accent: 'border-white/10 bg-slate-950/36 text-white'
    }
  }

  if (item?.kind === 'ai') {
    return {
      initials: 'IA',
      accent: 'border-amber-300/25 bg-amber-300/15 text-amber-100'
    }
  }

  if (item?.kind === 'user' || item?.kind === 'invite') {
    return {
      initials: getInitialsFromName(currentUserName || 'TU'),
      accent: 'border-sky-300/20 bg-sky-400/14 text-sky-100'
    }
  }

  return {
    initials: item?.kind === 'task' ? 'TK' : 'NT',
    accent: 'border-white/10 bg-slate-950/36 text-white'
  }
}

function parseParticipantMetadata(value) {
  if (!value) return {}

  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function getInitialsFromName(value) {
  return String(value || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'TU'
}
